import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { Flame, X } from "lucide-react";

/**
 * Slim spotlight teaser chip pinned to the top of the Plyx video feed.
 * Single line: "🔥 Country of the Week: Mali · 6d left · Enter →".
 * Dismissable for the current session (localStorage flag for 24h).
 *
 * Doesn't break the swipeable feed — sits over the top safe area, tap to go
 * straight to the challenge detail page.
 */

const DISMISS_KEY = "plyx_spotlight_chip_dismissed_until";

const fmtShortCountdown = (endsAtIso) => {
  if (!endsAtIso) return null;
  const ms = new Date(endsAtIso).getTime() - Date.now();
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days >= 1) return `${days}d left`;
  if (hours >= 1) return `${hours}h left`;
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return `${mins}m left`;
};

const FeedSpotlightChip = () => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    // Honour 24h dismissal.
    try {
      const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (until && until > Date.now()) return;
    } catch { /* ignore */ }
    plyxApi
      .get("/plyx/challenges/weekly/featured")
      .then((r) => {
        const d = r?.data || {};
        // Prefer country, fall back to club. Either alone is a great teaser.
        const pick = d.country_challenge || d.club_challenge;
        if (pick?.challenge_id) {
          setData(pick);
          setHidden(false);
        }
      })
      .catch(() => { /* silent */ });
  }, []);

  if (hidden || !data) return null;

  const onDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHidden(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch { /* ignore */ }
  };

  const countdown = fmtShortCountdown(data.ends_at);
  // Trim the title for the slim chip.
  const compactTitle = (data.title || "").replace(/^[^:]+:\s*/, "");

  return (
    <Link
      to={`/plyx/challenges/${data.challenge_id}`}
      data-testid="plyx-feed-spotlight-chip"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 max-w-[88%] px-3 py-1.5 rounded-full text-[12px] font-semibold text-white bg-black/65 backdrop-blur-md border border-white/15 shadow-lg hover:bg-black/80 transition-colors"
      style={{
        backgroundImage:
          "linear-gradient(135deg, rgba(255,45,122,0.35), rgba(255,180,45,0.25) 60%, rgba(0,224,199,0.25))",
      }}
    >
      <Flame className="w-3.5 h-3.5 text-[#FFB42D] shrink-0" />
      <span className="truncate">
        <span className="text-white/85">Spotlight ·</span>{" "}
        <span className="font-bold">{compactTitle}</span>
        {countdown && <span className="text-white/75"> · {countdown}</span>}
      </span>
      <span className="ml-1 text-[11px] font-bold opacity-95 shrink-0">Enter →</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss spotlight"
        data-testid="plyx-feed-spotlight-dismiss"
        className="ml-1 -mr-1 p-0.5 rounded-full opacity-60 hover:opacity-100 hover:bg-white/15 shrink-0"
      >
        <X className="w-3 h-3" />
      </button>
    </Link>
  );
};

export default FeedSpotlightChip;
